import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { Estimation } from '../../../models/estimacion.model';
import { User } from '../../../models/user.model';

import { SlideOverComponent } from '../../../shared/slide-over/slide-over.component';
import { ExportService, ExporterDescriptorAuth } from '../../services/export.service';
import { ExportPayload } from '../../models/export-payload';
import { ExportProvenance } from '../../models/provenance';
import { IntegrationConnectionChipComponent } from '../../../integrations/integration-connection-chip.component';

/**
 * Panel de exportación: contenido del slide-over.
 *
 * Estructura:
 *   1. Preview compacto con totales y conteo por origen.
 *   2. Selector de destino (chips con icono).
 *   3. Bloque de opciones:
 *        a. Chip de conexión OAuth (si descriptor.requiresIntegration).
 *        b. Options form específico del exporter, montado dinámicamente
 *           con ViewContainerRef (si descriptor.optionsComponent).
 *        c. Toggle "Incluir campos completos" (si descriptor.supportsExtras).
 *   4. CTA "Exportar" en el footer.
 *
 * Refactor Punto 7: añadida mecánica `optionsComponent` dinámico. Usamos
 * `ViewContainerRef.createComponent()` en vez de `ngComponentOutlet` porque
 * el segundo no soporta capturar @Output del componente dinámico.
 *
 * @author Marcos Cordo
 * @version 07/06/2026_2
 */
@Component({
  selector: 'app-export-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, SlideOverComponent, IntegrationConnectionChipComponent],
  templateUrl: './export-panel.component.html',
  styleUrls: ['./export-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExportPanelComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Input() open = false;
  @Input() estimation: Estimation | null = null;
  @Input() user: User | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() exported = new EventEmitter<{ exporterId: string; filename?: string }>();
  @Output() enqueued = new EventEmitter<{ jobId: string; exporterId: string; exporterLabel: string }>();
  @Output() failed = new EventEmitter<{ exporterId: string; error: string }>();

  /** Host donde se monta el optionsComponent dinámico. */
  @ViewChild('optionsHost', { read: ViewContainerRef })
  private optionsHost?: ViewContainerRef;

  payload: ExportPayload | null = null;
  descriptors: ExporterDescriptorAuth[] = [];
  selectedId: string | null = null;
  running = false;
  includeExtras = false;

  /** Opciones recogidas del componente dinámico — se envían con el job. */
  private dynamicOptions: unknown = {};
  /** Validez del componente dinámico — si false, el CTA queda deshabilitado. */
  optionsValid = true;     // los exporters sin optionsComponent siempre son válidos

  /** Referencia al componente dinámico vivo y sus suscripciones. */
  private optionsCompRef: ComponentRef<unknown> | null = null;
  private optionsSubs: Subscription[] = [];

  /** Snapshot anterior para saber si re-montar el dynamic component. */
  private mountedFor: string | null = null;

  readonly ExportProvenance = ExportProvenance;

  private exportService = inject(ExportService);
  private cdr = inject(ChangeDetectorRef);

  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      if (this.open) {
        this.refresh();
      } else {
        this.resetState();
      }
    }
  }

  ngAfterViewInit(): void {
    // Si el panel se abrió antes de que ViewChild estuviera listo, montamos ahora.
    //queueMicrotask(() => this.syncOptionsComponent());
    setTimeout(() => this.syncOptionsComponent(), 0)
  }

  ngOnDestroy(): void {
    this.destroyOptionsComponent();
  }

  private refresh(): void {
    this.descriptors = this.exportService.getAllDescriptors();
    this.payload = this.estimation
      ? this.exportService.buildPayload(this.estimation, this.user)
      : null;
    this.cdr.markForCheck();
  }

  private resetState(): void {
    this.selectedId = null;
    this.running = false;
    this.includeExtras = false;
    this.dynamicOptions = {};
    this.optionsValid = true;
    this.destroyOptionsComponent();
  }

  // ==========================================================================
  // SELECCIÓN DE DESTINO
  // ==========================================================================

  selectDestination(id: string): void {
    const d = this.descriptors.find(x => x.id === id);
    if (!d || !d.authorized) return;
    this.selectedId = id;
    if (!d.supportsExtras) this.includeExtras = false;
    // Reiniciamos validez — la confirmará el dynamic component si lo tiene.
    this.optionsValid = !d.optionsComponent;
    this.dynamicOptions = {};
    this.cdr.markForCheck();
    // El ViewChild #optionsHost vive bajo un *ngIf="selectedId"; en el primer
    // change-detection puede no estar todavía. queueMicrotask espera al render.
    //queueMicrotask(() => this.syncOptionsComponent());
    setTimeout(() => this.syncOptionsComponent(), 0)
  }

  get selectedDescriptor(): ExporterDescriptorAuth | null {
    if (!this.selectedId) return null;
    return this.descriptors.find(d => d.id === this.selectedId) ?? null;
  }

  get supportsExtrasToggle(): boolean {
    return !!this.selectedDescriptor?.supportsExtras;
  }

  get hasOptionsForSelected(): boolean {
    const d = this.selectedDescriptor;
    return this.supportsExtrasToggle || !!d?.optionsComponent;
  }

  get hasAvailableDestinations(): boolean {
    return this.descriptors.length > 0;
  }

  provenanceCount(p: ExportProvenance): number {
    return this.payload?.summary.provenanceCounts[p] ?? 0;
  }

  get canExport(): boolean {
    if (this.running || !this.selectedId || !this.payload) return false;
    const d = this.selectedDescriptor;
    if (!d || !d.authorized) return false;
    return this.optionsValid;
  }

  // ==========================================================================
  // MONTAJE DEL OPTIONS COMPONENT DINÁMICO
  // ==========================================================================

  private syncOptionsComponent(): void {
    const d = this.selectedDescriptor;
    const targetId = d?.optionsComponent ? d.id : null;
    if (targetId === this.mountedFor) return;        // ya montado para este destino

    this.destroyOptionsComponent();

    if (!d?.optionsComponent || !this.optionsHost) {
      this.mountedFor = null;
      return;
    }

    this.optionsCompRef = this.optionsHost.createComponent(d.optionsComponent);
    this.mountedFor = d.id;

    const inst = this.optionsCompRef.instance as Record<string, unknown>;

    const onOptionsChanged = inst['optionsChanged'];
    if (onOptionsChanged && typeof (onOptionsChanged as EventEmitter<unknown>).subscribe === 'function') {
      const sub = (onOptionsChanged as EventEmitter<unknown>).subscribe(opts => {
        this.dynamicOptions = opts;
        this.cdr.markForCheck();
      });
      this.optionsSubs.push(sub);
    }

    const onValidityChanged = inst['validityChanged'];
    if (onValidityChanged && typeof (onValidityChanged as EventEmitter<boolean>).subscribe === 'function') {
      const sub = (onValidityChanged as EventEmitter<boolean>).subscribe(valid => {
        this.optionsValid = !!valid;
        this.cdr.markForCheck();
      });
      this.optionsSubs.push(sub);
    }
  }

  private destroyOptionsComponent(): void {
    this.optionsSubs.forEach(s => s.unsubscribe());
    this.optionsSubs = [];
    this.optionsCompRef?.destroy();
    this.optionsCompRef = null;
    this.mountedFor = null;
  }

  // ==========================================================================
  // CHIP DE INTEGRACIÓN
  // ==========================================================================

  /**
   * El chip emite (connectionChanged) tras conectar/desconectar OAuth.
   * Re-cargamos el options component si lo hay — JiraOptions re-fetcha
   * proyectos automáticamente porque su ngOnInit corre de nuevo al re-crearse.
   */
  onIntegrationConnectionChanged(): void {
    // Forzamos re-monte del options component (con ngOnInit fresco).
    this.mountedFor = null;
    this.syncOptionsComponent();
  }

  // ==========================================================================
  // EJECUCIÓN
  // ==========================================================================

  async runExport(): Promise<void> {
    if (!this.canExport) return;
    if (!this.selectedId || !this.payload) return;

    const d = this.selectedDescriptor;
    if (!d) return;

    this.running = true;
    this.cdr.markForCheck();

    try {
      let effectivePayload = this.payload;
      if (this.includeExtras && this.supportsExtrasToggle) {
        effectivePayload = await this.exportService.enrichPayloadWithExtras(this.payload);
      }

      if (d.mode === 'sync') {
        const artifact = await this.exportService.exportSync(this.selectedId, effectivePayload, this.dynamicOptions);
        await this.exportService.triggerDownload(artifact);
        this.exported.emit({ exporterId: this.selectedId, filename: artifact.filename });
      } else {
        const enqueueResp = await this.exportService.startAsyncJob({
          exporterId: this.selectedId,
          estimationId: effectivePayload.estimation.id,
          options: this.dynamicOptions ?? {},
          payload: effectivePayload
        });
        this.enqueued.emit({
          jobId: enqueueResp.jobId,
          exporterId: this.selectedId,
          exporterLabel: d.label
        });
      }

      this.running = false;
      this.cdr.markForCheck();
      this.requestClose();
    } catch (err: any) {
      this.failed.emit({
        exporterId: this.selectedId,
        error: err?.message ?? 'Error desconocido'
      });
      this.running = false;
      this.cdr.markForCheck();
    }
  }

  requestClose(): void {
    if (this.running) return;
    this.closed.emit();
  }
}
